import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ArrowDown, Radio } from "lucide-react";
import { useParallax } from "../../hooks/useParallax";
import WindLayer from "./WindLayer";

const HERO_IMG =
  "https://images.pexels.com/photos/10813724/pexels-photo-10813724.jpeg?auto=compress&cs=tinysrgb&w=1920";

export default function Hero() {
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState(null);
  const bgRef = useParallax(0.4);
  const gridRef = useParallax(0.18);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/server/stats")
      .then((res) => !cancelled && setStats(res.data))
      .catch(() => {});
    api
      .get("/content/home")
      .then((res) => !cancelled && setContent(res.data?.content || null))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Fallback defaults keep the page readable if the API is slow or the DB
  // has no override yet.
  const c = content || {};
  const g = (k, dflt) => (c[k] != null && c[k] !== "" ? c[k] : dflt);

  const playerText = (() => {
    if (!stats) return "—";
    if (stats.server_status !== "online") return "—";
    return `${stats.players_online}/${stats.max_players}`;
  })();

  const slotsLeft = stats
    ? Math.max(stats.whitelist_slots_total - stats.whitelist_slots_taken, 0)
    : null;

  const statusLabel = (() => {
    if (!stats) return "—";
    return stats.server_status === "online" ? "ONLINE" : "OFFLINE/UNKNOWN";
  })();
  const statusOnline = stats?.server_status === "online";

  return (
    <section id="home" className="relative min-h-[92vh] flex items-center overflow-hidden">
      {/* Background image — parallax (outer) + wind sway (inner) */}
      {/* Parallax wrapper — transform only, no animation */}
<div ref={bgRef} className="absolute -inset-y-24 inset-x-0">
  {/* Wind sway wrapper — animation only, no transform conflict */}
  <div className="absolute inset-0 wind-sway-wrapper">
    <div
      className="absolute inset-0 bg-cover bg-center wind-sway"
      style={{ backgroundImage: `url(${HERO_IMG})` }}
      aria-hidden="true"
    />
  </div>
</div>
      <div className="absolute inset-0 bg-[#0D110C]/80" />
      <WindLayer />
      <div ref={gridRef} className="absolute -inset-y-16 inset-x-0 tactical-grid opacity-60" />
      <div className="absolute inset-0 vignette" />
      <div className="absolute inset-0 scanlines" />

      {/* Top stat strip */}
      <div
        className="absolute left-0 right-0 z-10"
        style={{ top: "calc(88px + var(--announcement-h, 0px))" }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center gap-6 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#8D9A87]">
            <span className="flex items-center gap-2">
              <Radio size={12} className="text-[#C0A875]" /> {g("stat_coords", "38.4N // 85.7W")}
            </span>
            <span className="hidden sm:inline">{g("stat_region", "— KENTUCKY, EXCLUSION ZONE")}</span>
            <span className="ml-auto">{g("stat_era", "YEAR 10 // POST-OUTBREAK")}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-40 pb-24">
        <div className="max-w-4xl fade-up">
          <h1
            className="font-display uppercase text-[#E2E4DF] text-5xl sm:text-7xl lg:text-[8rem] leading-[0.95] tracking-tighter font-bold"
            data-testid="hero-title"
          >
            {g("title_line1", "What's")}<br />
            <span className="text-[#C0A875] flicker">{g("title_line2_prefix", "Left")}</span> {g("title_line2_suffix", "of Us")}
          </h1>
          <p className="mt-8 max-w-2xl text-[#8D9A87] text-base sm:text-lg leading-relaxed">
            {g("description", "A decade after the Knox Event, the wind still carries the smell of damp ash. Survivors broker scraps, weather hostile winters, and remember the old world only in dreams. This is a slow-burn, character-first RP server. Will you be the next survivor who makes a difference?")}
          </p>

          {/* Stats strip */}
          <div
            data-testid="hero-stats"
            className="mt-10 inline-flex flex-wrap items-stretch border border-[#283524] bg-[#0D110C]/85 backdrop-blur"
          >
            <Stat
              label="Players Online"
              value={playerText}
              testid="stat-players"
            />
            <Stat
              label="Whitelist Slots"
              value={
                slotsLeft !== null ? (
                  <span className={slotsLeft <= 5 ? "text-[#C0392B]" : ""}>
                    {slotsLeft} LEFT
                  </span>
                ) : (
                  "—"
                )
              }
              testid="stat-whitelist"
            />
            <Stat
              label="Status"
              value={
                <span className="flex items-center gap-2">
                  <span
                    className={`w-2 h-2 pulse-dot ${
                      statusOnline ? "bg-[#9CC279]" : "bg-[#C0392B]"
                    }`}
                  />
                  {statusLabel}
                </span>
              }
              testid="stat-status"
            />
            <Stat
              label="Restarts (US Eastern)"
              value={<NextRestart />}
              testid="stat-restarts"
              last
            />
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="/apply"
              data-testid="hero-apply-cta"
              className="btn-shine bg-[#C0A875] hover:bg-[#a99362] text-[#0D110C] px-7 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold transition-colors"
            >
              {g("cta_primary", "Apply for Whitelist →")}
            </a>
            <a
              href="#lore"
              data-testid="hero-lore-cta"
              className="border border-[#5C6B51] hover:border-[#C0A875] text-[#E2E4DF] hover:text-[#C0A875] px-7 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] transition-colors"
            >
              {g("cta_secondary", "Read the Lore")}
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#8D9A87]">
        <span className="label-tag">{g("scroll_hint", "SCROLL // DESCEND")}</span>
        <ArrowDown size={16} className="animate-bounce" />
      </div>
    </section>
  );
}

function Stat({ label, value, testid, last = false }) {
  return (
    <div
      data-testid={testid}
      className={`px-5 py-4 ${!last ? "border-r border-[#283524]" : ""}`}
    >
      <div className="label-tag mb-1">{label}</div>
      <div className="font-mono-ui text-base text-[#E2E4DF] font-semibold tracking-wider">
        {value}
      </div>
    </div>
  );
}

/**
 * Live "Restarts (EST)" stat: shows the four PZ server restart times
 * (5am · 11am · 5pm · 11pm EST) with the next-upcoming one highlighted
 * + a tiny countdown ("in 1h 24m"). Recalculates every minute.
 */
const RESTART_HOURS_EST = [5, 11, 17, 23];

function NextRestart() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Convert "now" to the America/New_York wall clock, which handles EST/EDT
  // automatically. Intl.DateTimeFormat is the reliable cross-browser way.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(now);
  const estHour = Number(parts.find((p) => p.type === "hour")?.value || 0) % 24;
  const estMin = Number(parts.find((p) => p.type === "minute")?.value || 0);

  let nextHour = RESTART_HOURS_EST.find((h) => h > estHour || (h === estHour && estMin === 0));
  if (nextHour === undefined) nextHour = RESTART_HOURS_EST[0]; // wraps to tomorrow 5am
  const minsUntil = (((nextHour - estHour + 24) % 24) * 60 - estMin + 1440) % 1440;
  const hLeft = Math.floor(minsUntil / 60);
  const mLeft = minsUntil % 60;

  return (
    <span className="flex flex-col items-start gap-0.5 leading-tight">
      <span className="flex gap-1.5">
        {RESTART_HOURS_EST.map((h) => {
          const isNext = h === nextHour;
          const label = h === 0 ? "12a" : h < 12 ? `${h}a` : h === 12 ? "12p" : `${h - 12}p`;
          return (
            <span
              key={h}
              className={isNext ? "text-[#C0A875] font-semibold" : "text-[#5C6B51]"}
            >
              {label}
            </span>
          );
        })}
      </span>
      {/* Regular color normally, but flip to red inside the last 30 min so
          it reads as an alert when a restart is close. */}
      <span
        className={`font-mono-ui text-[10px] tracking-[0.15em] font-semibold ${
          hLeft === 0 && mLeft <= 30 ? "text-[#C0392B]" : "text-[#8D9A87]"
        }`}
      >
        Next restart in {hLeft > 0 ? `${hLeft}h ` : ""}{mLeft}m
      </span>
    </span>
  );
}

